/**
 * oddit-section-main-product
 * Handles accordion toggling, purchase option selection, and thumbnail selection.
 * Supports Shopify theme editor section lifecycle events.
 */
(function () {
  'use strict';

  /**
   * shopify-payment-terms renders in a closed shadow tree; theme CSS cannot target it.
   * These rules mirror .oddit-section-main-product__installments (custom) typography/layout.
   */
  var PAYMENT_TERMS_SHADOW_CSS = [
    '#shopify-installments {',
    '  align-items: center !important;',
    '  text-align: center;',
    '  font-family: Helvetica, Arial, sans-serif !important;',
    '  gap: 6px !important;',
    '}',
    '#shopify-installments p {',
    '  display: flex !important;',
    '  flex-direction: column !important;',
    '  align-items: center !important;',
    '  gap: 6px !important;',
    '  margin: 0 !important;',
    '  text-align: center !important;',
    '  max-width: 100%;',
    '}',
    '#shopify-installments-content {',
    '  font-size: 13px !important;',
    '  font-weight: 400 !important;',
    '  color: rgba(0, 0, 0, 0.6) !important;',
    '  line-height: normal !important;',
    '  text-align: center !important;',
    '  padding-right: 0 !important;',
    '}',
    '#shopify-installments .text-purple-primary {',
    '  color: #5f37f4 !important;',
    '}',
    '#shopify-installments p .inline-flex.align-middle svg {',
    '  height: 10px !important;',
    '  width: auto !important;',
    '  vertical-align: middle;',
    '}',
    '#shopify-installments-cta,',
    '[data-testid="shopify-installments-prequal-cta"] {',
    '  font-size: 11px !important;',
    '  font-weight: 400 !important;',
    '  color: rgba(0, 0, 0, 0.6) !important;',
    '  line-height: normal !important;',
    '  text-decoration: underline !important;',
    '}',
    '#shopify-installments-cta:hover,',
    '[data-testid="shopify-installments-prequal-cta"]:hover {',
    '  opacity: 0.7;',
    '}',
  ].join('\n');

  function injectOdditPaymentTermsShadowStyles(shopifyPaymentTermsEl) {
    if (!shopifyPaymentTermsEl || !shopifyPaymentTermsEl.shadowRoot) return;
    var root = shopifyPaymentTermsEl.shadowRoot;
    var style = root.getElementById('oddit-payment-terms-theme-overrides');
    if (!style) {
      style = document.createElement('style');
      style.id = 'oddit-payment-terms-theme-overrides';
      root.appendChild(style);
    }
    style.textContent = PAYMENT_TERMS_SHADOW_CSS;
  }

  function setupOdditPaymentTermsStyling(section) {
    var terms = section.querySelector('.oddit-section-main-product__dynamic-installments shopify-payment-terms');
    if (!terms) return;

    var attachMo = null;
    var attrMo = null;
    var attachTimeoutId = null;

    function scheduleInject() {
      injectOdditPaymentTermsShadowStyles(terms);
    }

    customElements.whenDefined('shopify-payment-terms').then(function () {
      scheduleInject();

      if (!terms.shadowRoot) {
        attachMo = new MutationObserver(function () {
          if (terms.shadowRoot) {
            scheduleInject();
            if (attachMo) attachMo.disconnect();
            attachMo = null;
          }
        });
        attachMo.observe(terms, { childList: true, subtree: true });
        attachTimeoutId = setTimeout(function () {
          if (attachMo) attachMo.disconnect();
          attachMo = null;
        }, 8000);
      }

      attrMo = new MutationObserver(function () {
        setTimeout(scheduleInject, 50);
      });
      attrMo.observe(terms, { attributes: true, attributeFilter: ['variant-id'] });
    });

    return function destroyPaymentTermsStyling() {
      if (attachMo) attachMo.disconnect();
      if (attrMo) attrMo.disconnect();
      if (attachTimeoutId) clearTimeout(attachTimeoutId);
    };
  }

  function initSection(container) {
    var section = container || document.querySelector('.oddit-section-main-product');
    if (!section) return;

    var handlers = [];

    function addListener(el, event, fn) {
      el.addEventListener(event, fn);
      handlers.push({ el: el, event: event, fn: fn });
    }

    /* ---------- Accordions ---------- */
    var accordions = section.querySelectorAll('[data-accordion]');
    accordions.forEach(function (accordion) {
      var header = accordion.querySelector('.oddit-section-main-product__accordion-header');
      if (!header) return;

      addListener(header, 'click', function () {
        var isOpen = accordion.classList.contains('oddit-section-main-product__accordion--open');

        if (isOpen) {
          accordion.classList.remove('oddit-section-main-product__accordion--open');
          header.setAttribute('aria-expanded', 'false');
        } else {
          accordion.classList.add('oddit-section-main-product__accordion--open');
          header.setAttribute('aria-expanded', 'true');
        }
      });
    });

    /* ---------- Purchase Option Selection ---------- */
    var options = section.querySelectorAll('.oddit-section-main-product__option');
    options.forEach(function (option) {
      addListener(option, 'click', function () {
        options.forEach(function (o) {
          o.classList.remove('oddit-section-main-product__option--active');
          var radio = o.querySelector('.oddit-section-main-product__radio-input');
          if (radio) radio.checked = false;
        });

        option.classList.add('oddit-section-main-product__option--active');
        var radio = option.querySelector('.oddit-section-main-product__radio-input');
        if (radio) radio.checked = true;
      });
    });

    /* ---------- Variant Option Picker ---------- */
    var variantsDataEl = section.querySelector('[data-product-variants]');
    var optionsDataEl = section.querySelector('[data-product-options]');

    if (variantsDataEl && optionsDataEl) {
      var variants = JSON.parse(variantsDataEl.textContent);
      var optionNames = JSON.parse(optionsDataEl.textContent);
      var selectedOptions = [];

      // Initialize selectedOptions from current active buttons
      var optionGroups = section.querySelectorAll('[data-variant-option-group]');
      optionGroups.forEach(function (group, i) {
        var activeBtn = group.querySelector('.oddit-section-main-product__variant-option-btn--active');
        selectedOptions[i] = activeBtn ? activeBtn.getAttribute('data-option-value') : '';
      });

      function findVariant(options) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var match = true;
          for (var j = 0; j < options.length; j++) {
            if (v.options[j] !== options[j]) {
              match = false;
              break;
            }
          }
          if (match) return v;
        }
        return null;
      }

      function updateVariant(newVariant) {
        if (!newVariant) return;

        // Update hidden variant ID input
        var variantInput = section.querySelector('input[name="id"]');
        if (variantInput) variantInput.value = newVariant.id;

        // Update title variant text
        var titleVariant = section.querySelector('.oddit-section-main-product__title-variant');
        if (titleVariant) {
          if (newVariant.title && newVariant.title !== 'Default Title') {
            titleVariant.textContent = '(' + newVariant.title + ')';
            titleVariant.style.display = '';
          } else {
            titleVariant.style.display = 'none';
          }
        } else if (newVariant.title && newVariant.title !== 'Default Title') {
          var titleEl = section.querySelector('.oddit-section-main-product__title');
          if (titleEl && !titleEl.querySelector('.oddit-section-main-product__title-variant')) {
            var span = document.createElement('span');
            span.className = 'oddit-section-main-product__title-variant';
            span.textContent = ' (' + newVariant.title + ')';
            titleEl.appendChild(span);
          }
        }

        // Update price
        var priceEl = section.querySelector('.oddit-section-main-product__price');
        if (priceEl) {
          priceEl.textContent = '$' + (newVariant.price / 100).toFixed(2);
        }

        // Update compare price
        var comparePriceEl = section.querySelector('.oddit-section-main-product__compare-price');
        var saveBadge = section.querySelector('.oddit-section-main-product__save-badge');
        if (newVariant.compare_at_price && newVariant.compare_at_price > newVariant.price) {
          if (comparePriceEl) {
            comparePriceEl.textContent = '$' + (newVariant.compare_at_price / 100).toFixed(2);
            comparePriceEl.style.display = '';
          }
          if (saveBadge) {
            var savePercent = Math.round(((newVariant.compare_at_price - newVariant.price) / newVariant.compare_at_price) * 100);
            saveBadge.textContent = 'Save ' + savePercent + '%';
            saveBadge.style.display = '';
          }
        } else {
          if (comparePriceEl) comparePriceEl.style.display = 'none';
          if (saveBadge) saveBadge.style.display = 'none';
        }

        // Update add-to-cart button
        var addToCartBtn = section.querySelector('.oddit-section-main-product__add-to-cart');
        if (addToCartBtn) {
          if (newVariant.available) {
            addToCartBtn.disabled = false;
            addToCartBtn.innerHTML = '<span class="oddit-section-main-product__atc-bold">ADD TO BAG</span>&nbsp;&nbsp;&bull;&nbsp;&nbsp;$' + (newVariant.price / 100).toFixed(2);
          } else {
            addToCartBtn.disabled = true;
            addToCartBtn.innerHTML = '<span class="oddit-section-main-product__atc-bold">SOLD OUT</span>';
          }
        }

        // Update URL without reload
        if (window.history && window.history.replaceState) {
          var url = new URL(window.location);
          url.searchParams.set('variant', newVariant.id);
          window.history.replaceState({}, '', url.toString());
        }

        setTimeout(function () {
          var termsEl = section.querySelector('.oddit-section-main-product__dynamic-installments shopify-payment-terms');
          if (termsEl) injectOdditPaymentTermsShadowStyles(termsEl);
        }, 100);
      }

      section.querySelectorAll('.oddit-section-main-product__variant-option-btn').forEach(function (btn) {
        addListener(btn, 'click', function () {
          var optionIndex = parseInt(btn.getAttribute('data-option-index'), 10);
          var optionValue = btn.getAttribute('data-option-value');

          selectedOptions[optionIndex] = optionValue;

          // Update active state on buttons in this group
          var group = btn.closest('[data-variant-option-group]');
          group.querySelectorAll('.oddit-section-main-product__variant-option-btn').forEach(function (b) {
            b.classList.remove('oddit-section-main-product__variant-option-btn--active');
          });
          btn.classList.add('oddit-section-main-product__variant-option-btn--active');

          // Update displayed selected value
          var selectedLabel = group.querySelector('[data-selected-option-value]');
          if (selectedLabel) selectedLabel.textContent = optionValue;

          // Find and apply the matching variant
          var newVariant = findVariant(selectedOptions);
          if (newVariant) {
            updateVariant(newVariant);
          }
        });
      });
    }

    /* ---------- Subscription Selector ---------- */
    var subscriptionSelector = section.querySelector('[data-subscription-selector]');
    if (subscriptionSelector) {
      var radios = subscriptionSelector.querySelectorAll('.oddit-subscription-selector__radio');
      var detailsPanel = subscriptionSelector.querySelector('[data-subscription-details]');
      var sellingPlanInput = subscriptionSelector.querySelector('[data-selling-plan-input]');
      var formSellingPlanInput = section.querySelector('[data-selling-plan-form-input]');

      radios.forEach(function (radio) {
        addListener(radio, 'change', function () {
          var purchaseType = radio.getAttribute('data-purchase-type');
          var planId = radio.getAttribute('data-selling-plan') || '';

          if (sellingPlanInput) sellingPlanInput.value = planId;
          if (formSellingPlanInput) formSellingPlanInput.value = planId;

          if (detailsPanel) {
            if (purchaseType === 'subscription') {
              detailsPanel.removeAttribute('hidden');
            } else {
              detailsPanel.setAttribute('hidden', '');
            }
          }

          subscriptionSelector.querySelectorAll('.oddit-subscription-selector__option').forEach(function (opt) {
            opt.classList.remove('oddit-subscription-selector__option--active');
          });
          radio.closest('.oddit-subscription-selector__option').classList.add('oddit-subscription-selector__option--active');
        });
      });

      var frequencySelect = subscriptionSelector.querySelector('[data-action="update-selling-plan"]');
      if (frequencySelect) {
        addListener(frequencySelect, 'change', function () {
          var newPlanId = frequencySelect.value;
          if (sellingPlanInput) sellingPlanInput.value = newPlanId;
          if (formSellingPlanInput) formSellingPlanInput.value = newPlanId;

          var subscribeRadio = subscriptionSelector.querySelector('[data-purchase-type="subscription"]');
          if (subscribeRadio) {
            subscribeRadio.value = newPlanId;
            subscribeRadio.setAttribute('data-selling-plan', newPlanId);
          }
        });
      }
    }

    /* ---------- Add to Cart → Cart Drawer ---------- */
    var productForms = section.querySelectorAll('form[action*="/cart/add"]');
    productForms.forEach(function (form) {
      addListener(form, 'submit', function (e) {
        if (!window.OdditCartDrawer) return;
        e.preventDefault();

        var variantInput = form.querySelector('input[name="id"]');
        if (!variantInput) return;

        var variantId = Number(variantInput.value);
        if (!variantId) return;

        var sellingPlanFormInput = form.querySelector('[data-selling-plan-form-input]');
        var sellingPlanId = sellingPlanFormInput ? sellingPlanFormInput.value : '';

        var submitBtn = form.querySelector('[type="submit"]');
        if (submitBtn) submitBtn.disabled = true;

        window.OdditCartDrawer.addToCart(variantId, 1, {}, sellingPlanId || undefined)
          .then(function () {
            window.OdditCartDrawer.open();
          })
          .catch(function (err) {
            console.error('Add to cart failed:', err);
          })
          .finally(function () {
            if (submitBtn) submitBtn.disabled = false;
          });
      });
    });

    /* ---------- Thumbnail Selection & Slide Sync ---------- */
    var slidesContainer = section.querySelector('[data-slides]');
    var slides = section.querySelectorAll('.oddit-section-main-product__slide');
    var thumbnails = section.querySelectorAll('.oddit-section-main-product__thumbnail');
    var currentSlide = 0;

    function goToSlide(idx) {
      if (idx < 0 || idx >= slides.length) return;
      currentSlide = idx;

      /* Show/hide slides on desktop */
      slides.forEach(function (slide, i) {
        slide.style.display = (i === idx) ? '' : '';
        slide.classList.toggle('oddit-section-main-product__slide--active', i === idx);
      });

      /* On desktop, toggle display; on mobile, scroll */
      var isDesktop = window.matchMedia('(min-width: 990px)').matches;
      if (isDesktop) {
        slides.forEach(function (slide, i) {
          slide.style.display = (i === idx) ? 'block' : 'none';
        });
      } else if (slidesContainer && slides[idx]) {
        slidesContainer.scrollTo({ left: slides[idx].offsetLeft, behavior: 'smooth' });
      }

      /* Update active thumbnail */
      thumbnails.forEach(function (t) {
        t.classList.remove('oddit-section-main-product__thumbnail--active');
      });
      if (thumbnails[idx]) {
        thumbnails[idx].classList.add('oddit-section-main-product__thumbnail--active');
      }

      /* Update arrow visibility */
      var prevBtn = section.querySelector('[data-gallery-prev]');
      var nextBtn = section.querySelector('[data-gallery-next]');
      var hiddenClass = 'oddit-section-main-product__gallery-arrow--hidden';
      if (prevBtn) prevBtn.classList.toggle(hiddenClass, idx === 0);
      if (nextBtn) nextBtn.classList.toggle(hiddenClass, idx === slides.length - 1);
    }

    thumbnails.forEach(function (thumb) {
      addListener(thumb, 'click', function () {
        var idx = parseInt(thumb.getAttribute('data-index'), 10);
        goToSlide(idx);
      });
    });

    /* Navigation arrows */
    var prevBtn = section.querySelector('[data-gallery-prev]');
    var nextBtn = section.querySelector('[data-gallery-next]');

    if (prevBtn) {
      addListener(prevBtn, 'click', function () {
        goToSlide(currentSlide - 1);
      });
    }
    if (nextBtn) {
      addListener(nextBtn, 'click', function () {
        goToSlide(currentSlide + 1);
      });
    }

    /* Initialize arrow state */
    var hiddenClass = 'oddit-section-main-product__gallery-arrow--hidden';
    if (nextBtn && slides.length <= 1) nextBtn.classList.add(hiddenClass);

    /* Update active thumbnail on mobile scroll */
    if (slidesContainer) {
      var scrollTimeout;
      var scrollHandler = function () {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(function () {
          var containerLeft = slidesContainer.scrollLeft;
          var closest = 0;
          var closestDist = Infinity;
          slides.forEach(function (slide, i) {
            var dist = Math.abs(slide.offsetLeft - containerLeft);
            if (dist < closestDist) {
              closestDist = dist;
              closest = i;
            }
          });
          currentSlide = closest;
          thumbnails.forEach(function (t) {
            t.classList.remove('oddit-section-main-product__thumbnail--active');
          });
          if (thumbnails[closest]) {
            thumbnails[closest].classList.add('oddit-section-main-product__thumbnail--active');
          }
        }, 50);
      };
      addListener(slidesContainer, 'scroll', scrollHandler);
    }

    var destroyPaymentTermsStyling = setupOdditPaymentTermsStyling(section);

    /* Return a cleanup function */
    return function destroy() {
      handlers.forEach(function (h) {
        h.el.removeEventListener(h.event, h.fn);
      });
      handlers.length = 0;
      if (typeof destroyPaymentTermsStyling === 'function') destroyPaymentTermsStyling();
    };
  }

  /* ---------- Initial setup ---------- */
  var destroyFn = initSection();

  /* ---------- Theme Editor Support ---------- */
  document.addEventListener('shopify:section:load', function (event) {
    var container = event.target.querySelector('.oddit-section-main-product');
    if (container) {
      destroyFn = initSection(container);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (event.target.querySelector('.oddit-section-main-product') && typeof destroyFn === 'function') {
      destroyFn();
      destroyFn = null;
    }
  });
})();
